import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useAICalendar } from "contexts/AICalendarContext";
import _request from "actions/_request";
import AppContext from "AppContext";
import BuyCreditsModal from "components/BuyCreditsModal";
import { creditCostFor, extractCreditShortfall } from "utils/aiCredits";
import i18n from "i18";

/**
 * Step 4: Generate Posts
 * Generate post content with AI and poll status
 * @returns {JSX.Element} Step 4 Generation controls
 */
export default function Step4Generate() {
  const { state, nextStep, prevStep, setGeneratedPosts, setJobId } = useAICalendar();
  const { metaData, refreshCredits } = useContext(AppContext);
  const isTrial = metaData?.ai_credits ? metaData.ai_credits.monthly_allowance === 0 : false;

  // calendar_generate reserves credits up-front (heavier op → confirm first).
  const calendarCost = creditCostFor("calendar_generate");
  const availableBalance = metaData?.ai_credits?.balance ?? 0;

  const [generating, setGenerating] = useState(false);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [postsGenerated, setPostsGenerated] = useState(0);
  const [postsTotal, setPostsTotal] = useState(0);
  const [localError, setLocalError] = useState(null);
  
  // Generation settings
  const [creativity, setCreativity] = useState(0.7);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmoji, setIncludeEmoji] = useState(true);
  const [contentLength, setContentLength] = useState("medium");
  
  const pollingInterval = useRef(null);

  useEffect(() => {
    // Cleanup polling on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  const startPolling = (jobId) => {
    pollingInterval.current = setInterval(async () => {
      try {
        const response = await _request.aiCalendar.getGenerationStatus(jobId);
        
        if (response?.isOk && response?.data) {
          const status = response.data;
          
          if (status.progress !== undefined) {
            setProgress(status.progress);
          }
          if (status.posts_generated !== undefined) {
            setPostsGenerated(status.posts_generated);
          }
          if (status.posts_total !== undefined) {
            setPostsTotal(status.posts_total);
          }
          
          // Check if completed
          if (status.status === "completed") {
            setGeneratedPosts(status.posts || []);
            setGenerating(false);
            clearInterval(pollingInterval.current);
            nextStep();
          }
          
          // Check if failed
          if (status.status === "failed") {
            setLocalError(status.error || i18n.t("aiCalendarWizard.errors.generationFailed"));
            setGenerating(false);
            clearInterval(pollingInterval.current);
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
        // Continue polling unless it's a critical error
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleGenerate = async () => {
    setLocalError(null);
    setGenerating(true);
    setProgress(0);
    setPostsGenerated(0);
    setPostsTotal(0);

    try {
      const params = {
        schedule: state.schedule,
        topics: state.topics,
        business_context: state.businessContext,
        generation_settings: {
          creativity,
          include_hashtags: includeHashtags,
          include_emoji: includeEmoji,
          language: i18n.language,
          content_length: contentLength,
        },
        account_ids: state.selectedAccounts,
      };

      const response = await _request.aiCalendar.generatePosts(params);

      if (response?.isInsufficientCredits) {
        setCreditShortfall(extractCreditShortfall(response));
        setBuyCreditsOpen(true);
        setGenerating(false);
      } else if (response?.isOk && response?.data) {
        if (refreshCredits) refreshCredits();
        const jobId = response.data.job_id;
        setJobId(jobId);
        setPostsTotal(response.data.estimated_posts || 0);

        // Start polling
        startPolling(jobId);
      } else {
        setLocalError(response?.error || i18n.t("aiCalendarWizard.errors.generationStartFailed"));
        setGenerating(false);
      }
    } catch (error) {
      console.error("Generation start failed:", error);
      setLocalError(error.message || i18n.t("aiCalendarWizard.errors.generic"));
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">
        {i18n.t("aiCalendarWizard.step4.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n.t("aiCalendarWizard.step4.description")}
      </Typography>

      {localError && (
        <Alert severity="error" onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      {!generating && !state.generatedPosts.length && (
        <>
          {/* Generation Settings */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {i18n.t("aiCalendarWizard.step4.settings")}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                {i18n.t("aiCalendarWizard.step4.creativity")}: {creativity.toFixed(1)}
              </Typography>
              <Slider
                value={creativity}
                onChange={(e, val) => setCreativity(val)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                {i18n.t("aiCalendarWizard.step4.creativityHint")}
              </Typography>
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{i18n.t("aiCalendarWizard.step4.contentLength")}</InputLabel>
              <Select
                value={contentLength}
                label={i18n.t("aiCalendarWizard.step4.contentLength")}
                onChange={(e) => setContentLength(e.target.value)}
              >
                <MenuItem value="short">{i18n.t("aiCalendarWizard.step4.short")}</MenuItem>
                <MenuItem value="medium">{i18n.t("aiCalendarWizard.step4.medium")}</MenuItem>
                <MenuItem value="long">{i18n.t("aiCalendarWizard.step4.long")}</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={includeHashtags}
                  onChange={(e) => setIncludeHashtags(e.target.checked)}
                />
              }
              label={i18n.t("aiCalendarWizard.step4.includeHashtags")}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={includeEmoji}
                  onChange={(e) => setIncludeEmoji(e.target.checked)}
                />
              }
              label={i18n.t("aiCalendarWizard.step4.includeEmoji")}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={prevStep}>
              {i18n.t("aiCalendarWizard.back")}
            </Button>
            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={isTrial}
            >
              {i18n.t("aiCalendarWizard.step4.startGeneration")}
            </Button>
          </Box>
        </>
      )}

      {/* Confirm dialog — calendar generation reserves credits up-front (§14) */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "var(--background-menu)",
            color: "var(--text-color)",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>{i18n.t("aiCredits.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--text-color)" }}>
            {i18n.t("aiCredits.confirmReserveBody", { count: calendarCost })}
          </DialogContentText>
          <DialogContentText sx={{ color: "var(--text-color)", opacity: 0.7, mt: 1 }}>
            {i18n.t("aiCredits.confirmAvailable", { available: availableBalance })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: "var(--text-color)" }}>
            {i18n.t("aiCredits.confirmCancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              handleGenerate();
            }}
          >
            {i18n.t("aiCredits.confirmStart")}
          </Button>
        </DialogActions>
      </Dialog>

      <BuyCreditsModal
        open={buyCreditsOpen}
        onClose={() => {
          setBuyCreditsOpen(false);
          setCreditShortfall(null);
        }}
        isTrial={isTrial}
        shortfall={creditShortfall}
      />

      {generating && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", py: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="h6">
            {i18n.t("aiCalendarWizard.step4.generating")}
          </Typography>
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              {postsGenerated} / {postsTotal} {i18n.t("aiCalendarWizard.step4.posts")}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {i18n.t("aiCalendarWizard.step4.pleaseWait")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
